import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { anthropicService } from '../../services/anthropicService';
import { generateProductKnowledgePrompt, modules, pricingInfo } from '../../data/productModules';
import { getCachedResponse, cacheResponse, verifyCachePrewarmed } from '../../services/chatbotCacheSupabase';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const ProductAssistantWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Verify Supabase cache is pre-warmed on component mount
  useEffect(() => {
    verifyCachePrewarmed().catch(console.warn);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize with greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "👋 Hi! I'm here to answer questions about Explanatum. I can tell you about:\n\n• Our 23 AI-powered modules\n• Pricing and free trial\n• How modules work together\n• Comparisons with other tools\n\nWhat would you like to know?",
        timestamp: new Date()
      }]);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const userQuestion = input;
    setInput('');
    setIsLoading(true);

    try {
      // Check Supabase cache first to reduce API costs (shared across all users!)
      const cachedAnswer = await getCachedResponse(userQuestion);
      
      if (cachedAnswer) {
        // Use cached response - saved £0.01!
        const assistantMessage: Message = {
          role: 'assistant',
          content: cachedAnswer,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
        return;
      }

      // Cache miss - call API (will be cached for ALL future users)
      // Build conversation context (last 6 messages)
      const conversationHistory = messages.slice(-6).map(m => 
        `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`
      ).join('\n\n');

      // Generate full prompt with product knowledge
      const systemPrompt = generateProductKnowledgePrompt();
      const fullPrompt = `${systemPrompt}\n\n---\n\nConversation History:\n${conversationHistory}\n\nUser: ${userQuestion}\n\nAssistant (be concise, friendly, and helpful):`;

      const response = await anthropicService.generateText(fullPrompt);
      const finalResponse = response || "I'm here to help! Could you rephrase your question?";

      // Cache the response in Supabase for ALL future users
      await cacheResponse(userQuestion, finalResponse);

      const assistantMessage: Message = {
        role: 'assistant',
        content: finalResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Product assistant error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble right now. For immediate help, please contact support@explanatum.co.uk or start a free trial to explore the platform yourself!",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Quick action buttons
  const quickActions = [
    { label: 'What modules do you have?', question: 'What modules do you have?' },
    { label: 'How much does it cost?', question: 'How much does it cost?' },
    { label: 'How is this different?', question: 'How is this different from other tools?' },
  ];

  const handleQuickAction = (question: string) => {
    setInput(question);
    // Auto-send after short delay
    setTimeout(() => {
      sendMessage();
    }, 100);
  };

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-full p-4 shadow-2xl transition-all hover:scale-110 group animate-pulse hover:animate-none"
          aria-label="Open product assistant"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 bg-red-500 text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
            ?
          </span>
          <div className="absolute bottom-full right-0 mb-3 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-xl">
            Questions? Ask me! 💬
          </div>
        </button>
      )}

      {/* Chat widget */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl flex flex-col border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold flex items-center gap-2">
                  Explanatum Assistant
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                </div>
                <div className="text-xs text-blue-100">Typically responds instantly</div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded-full p-1.5 transition"
              aria-label="Close assistant"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.length === 1 && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-3 text-center">Quick questions:</p>
                <div className="flex flex-wrap gap-2">
                  {quickActions.map((action, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickAction(action.question)}
                      className="text-xs bg-white border border-blue-200 text-blue-700 px-3 py-2 rounded-full hover:bg-blue-50 hover:border-blue-400 transition"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-br-none'
                      : 'bg-white text-gray-900 rounded-bl-none shadow-sm border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  <span className={`text-xs mt-2 block ${msg.role === 'user' ? 'text-blue-100' : 'text-gray-400'}`}>
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about features, pricing..."
                className="flex-1 border border-gray-300 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 disabled:from-gray-300 disabled:to-gray-300 text-white rounded-full p-2.5 transition shadow-sm hover:shadow-md disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              Ready to get started? <a href="/signup" className="text-blue-600 hover:underline font-medium">Try free for 7 days →</a>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductAssistantWidget;
